import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, Ship } from "lucide-react";
import api from "../api";
import ExpenseDialog from "../components/ExpenseDialog";
import StatusBadge from "../components/StatusBadge";
import { fmtDate, fmtMoney } from "../utils/format";

export default function PortDouane() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/port-douane").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="port-douane-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Port & Douane</h1>
        <p className="mt-1 text-sm text-slate-500">Frais portuaires et de dédouanement — chaque frais augmente automatiquement le coût de revient du véhicule.</p>
      </div>

      {rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="port-douane-empty">
          <Anchor className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-slate-400">Aucun véhicule en transit, au port ou en dédouanement.</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {rows.map(({ vehicle: v, port_fees, customs_fees, fees }) => (
          <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`port-card-${v.internal_number}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <button onClick={() => navigate(`/vehicules/${v.id}`)} className="text-left">
                <p className="font-heading font-bold text-slate-900">{v.brand} {v.model} {v.year}</p>
                <p className="font-mono-num text-xs text-slate-400">{v.internal_number} · {v.vessel_name ? `Navire ${v.vessel_name}` : ""} {v.container_number ? `· Conteneur ${v.container_number}` : ""}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {v.departure_port || "?"} → {v.arrival_port || "?"} · Arrivée : {fmtDate(v.actual_arrival_date || v.port_arrival_date || v.eta_date)}
                </p>
              </button>
              <StatusBadge status={v.status} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-sky-50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-600">Frais de port</p>
                <p className="font-mono-num mt-1 text-sm font-bold text-sky-700">{fmtMoney(port_fees)}</p>
              </div>
              <div className="rounded-lg bg-orange-50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">Douane</p>
                <p className="font-mono-num mt-1 text-sm font-bold text-orange-700">{fmtMoney(customs_fees)}</p>
              </div>
              <div className="rounded-lg bg-cyan-50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-cyan-600">Coût de revient</p>
                <p className="font-mono-num mt-1 text-sm font-bold text-cyan-700">{fmtMoney(v.cost_basis)}</p>
              </div>
            </div>
            {fees.length > 0 && (
              <div className="mt-3 space-y-1">
                {fees.slice(0, 4).map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-xs">
                    <span className="text-slate-500">{fmtDate(f.date)} · {f.category}</span>
                    <span className="font-mono-num font-semibold">{fmtMoney(f.amount_fcfa)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <ExpenseDialog vehicleId={v.id} vehicleLabel={`${v.brand} ${v.model}`} defaultGroup="Port" onSaved={load} triggerLabel="Frais de port" />
              <ExpenseDialog vehicleId={v.id} vehicleLabel={`${v.brand} ${v.model}`} defaultGroup="Dédouanement" onSaved={load} triggerLabel="Frais de douane" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
